import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/PageShell';
import MediaCard from '@/components/feature/MediaCard';
import { mediaList } from '@/mocks/movies';
import { siteIntro } from '@/mocks/news';
import { usePageMeta } from '@/hooks/usePageMeta';
import HeroCarousel from '@/pages/home/components/HeroCarousel';
import QuickEntries from '@/pages/home/components/QuickEntries';
import NewsSection from '@/pages/home/components/NewsSection';
import TopicSection from '@/pages/home/components/TopicSection';

const sectionIds = ['m03', 'm01', 'm04', 'm14', 'm09', 'm22', 'm07', 'm18', 'm28', 'm24', 'm05', 'm29'];

export default function Home() {
  usePageMeta({
    title: '菠萝蜜网_最新电影电视剧资讯与影视推荐平台',
    description:
      '菠萝蜜网提供丰富的电影、电视剧资讯和影视内容推荐，覆盖多种热门影视作品，让用户轻松获取最新影视动态与精彩内容。',
    keywords: '菠萝蜜网,电影推荐,电视剧资讯,影视动态,影视内容,热门影视,影视平台',
    path: '/',
  });

  return (
    <SiteLayout>
      <div className="space-y-8">
        <HeroCarousel />
        <QuickEntries />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
          <div className="space-y-8">
            <section>
              <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
                <div className="flex items-end gap-3">
                  <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">近期推荐</h2>
                  <p className="pb-1 text-xs text-foreground-500">编辑团队本周挑选</p>
                </div>
                <Link to="/category/电影" className="flex items-center gap-1 whitespace-nowrap text-xs text-primary-700 transition hover:text-primary-800">
                  全部作品
                  <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center" />
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 xl:grid-cols-4">
                {sectionIds.slice(0, 8).map((id, index) => (
                  <MediaCard key={id} id={id} index={index} />
                ))}
              </div>
            </section>

            <NewsSection />
          </div>

          <div className="space-y-6">
            <section className="rounded-lg border border-background-200 bg-background-50">
              <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
                <h2 className="font-heading text-base font-bold text-foreground-950">正在热播</h2>
                <Link to="/category/电视剧" className="text-[11px] text-foreground-500 transition hover:text-primary-700">更多</Link>
              </div>
              <ul className="divide-y divide-background-100">
                {mediaList
                  .filter((item) => item.type === '电视剧')
                  .slice(0, 5)
                  .map((item, index) => (
                    <li key={item.id}>
                      <Link to={`/detail/${item.id}`} className="flex items-center gap-3 px-4 py-2.5 transition hover:bg-background-100">
                        <span className="w-4 text-center font-heading text-sm font-bold text-primary-500">{index + 1}</span>
                        <div className="h-14 w-10 shrink-0 overflow-hidden rounded bg-background-200">
                          <img src={item.poster} alt={item.title} title={item.title} className="h-full w-full object-cover" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[13px] text-foreground-900">{item.title}</p>
                          <p className="mt-0.5 truncate text-[11px] text-foreground-500">{item.genres.join(' / ')}</p>
                          <p className="mt-0.5 text-[10px] text-foreground-400">{item.runtime}</p>
                        </div>
                      </Link>
                    </li>
                  ))}
              </ul>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-100/60 p-4">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className="ri-quill-pen-line text-sm" />
                </span>
                <h2 className="font-heading text-base font-bold text-foreground-950">编辑手记</h2>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-foreground-600">
                “本周我们把《第七次日落》和《长夜锈河》放在一起重看了一遍，发现两部片子里最难拍的都是‘沉默’——一个人不发一言地盯着某处看。这种镜头在手机屏幕上会被快进，但在影院里，它是整部电影的重心。”
              </p>
              <p className="mt-2 text-right text-[11px] text-foreground-400">— 值班编辑 阿禾</p>
            </section>
          </div>
        </div>

        <TopicSection />

        <section className="rounded-xl bg-secondary-950 px-6 py-8 md:px-10 md:py-10">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.15fr_1fr] lg:items-center">
            <div>
              <p className="text-[11px] tracking-[0.2em] text-accent-400">ABOUT BOLUOMI</p>
              <h2 className="mt-2 font-heading text-xl font-bold text-background-50 md:text-2xl">{siteIntro.slogan}</h2>
              <p className="mt-3 max-w-xl text-xs leading-relaxed text-background-200/75 md:text-[13px]">{siteIntro.description}</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/about" className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition hover:bg-primary-600">
                  了解菠萝蜜网
                </Link>
                <Link to="/search?q=悬疑" className="whitespace-nowrap rounded-md border border-background-100/25 px-4 py-2 text-sm text-background-100 transition hover:border-accent-400 hover:text-accent-300">
                  试试搜索悬疑
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {siteIntro.stats.map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-100/12 bg-background-100/5 px-4 py-4">
                  <p className="font-heading text-xl font-bold text-accent-400">{stat.value}</p>
                  <p className="mt-1 text-[11px] text-background-200/70">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}